import React from "react";
import { ArrowRight } from "lucide-react";
import {
  FORM_WELCOME_ACTION,
  PRE_CONSULT_WELCOME_GREETING,
  PRE_CONSULT_WELCOME_HEADING,
  PRE_CONSULT_WELCOME_HEADING_TAIL,
  PRE_CONSULT_WELCOME_SUBTITLE,
} from "../../enquiries/publicFormCopy";

export interface PreconsultationWelcomeProps {
  /** Their first name where the workspace had one on file — see `toKnown`.
      Sets the line under the heading, and where there is none that line is
      left out rather than greeting nobody. */
  name?: string;
}

/**
 * What the link opens on: a greeting and a sentence about what is being asked.
 *
 * The button is not here. It belongs to the bottom edge of the screen rather
 * than to the words — see `PreconsultationWelcomeAction`, which the page hands
 * the shell as its floor.
 *
 * A screen of its own rather than the first step, because it is doing a
 * different job. Somebody has just tapped a link in a WhatsApp message and
 * does not yet know what it is; opening straight onto a page of forty fields
 * is how a form is closed again before it is read. This says what it is for in
 * one line, in the practitioner's voice, and asks for nothing.
 *
 * Their name where it is known, on the line under the heading. It is the
 * difference between a form that was sent to them and a form that was sent to
 * a list, and the practitioner does already know it — see `toKnown`, which is
 * careful never to guess one.
 *
 * Left-aligned on a phone and centred from `sm:` up, which is the enquiry
 * form's welcome exactly: a lockup pinned to the left edge of a 1400px window
 * looks lost, and text centred on a 360px one wastes the line. The two screens
 * are the same greeting from the same practitioner, reached from the same kind
 * of message, and a reader who meets both should not be able to tell they were
 * built apart.
 */
const PreconsultationWelcome: React.FC<PreconsultationWelcomeProps> = ({
  name,
}) => (
  <div className="sm:text-center">
    {/* Broken over two lines by hand rather than left to the box, so it falls
        the same way at every width — the design sets it as two lines and a
        heading that wrapped where the column happened to end would read as a
        mistake.

        The `short:` figures are the same screen on a handset with no height to
        spare — this card is not allowed to scroll, see `fit` on the shell, so
        the type gives ground rather than the button at the foot of it. The
        enquiry form's welcome gives the same ground at the same width. */}
    <h1 className="text-[40px] short:text-[32px] xl:text-[48px] font-semibold leading-[1.15] tracking-[-0.02em] text-[#0A4A52]">
      {PRE_CONSULT_WELCOME_HEADING}
      {/* The break is the phone's. There the two words are a heading stacked
          against the left edge; on a wide screen the design sets them on one
          centred line, and a hard break there would leave "Let’s" hanging
          over a column with room for ten more words. */}
      <br className="sm:hidden" />{" "}
      {PRE_CONSULT_WELCOME_HEADING_TAIL}
    </h1>

    {/* Who it is addressed to, under the heading rather than as it. Left out
        entirely where no first name is on file: "Hi," on its own is a greeting
        with its second half missing, which is worse than no greeting. */}
    {name && (
      <p className="mt-6 short:mt-4 text-[20px] short:text-[18px] sm:text-[22px] font-medium text-brand-dark">
        {PRE_CONSULT_WELCOME_GREETING} {name}
      </p>
    )}

    <p className="mt-3 short:mt-2 max-w-[420px] sm:max-w-[560px] sm:mx-auto text-[16px] sm:text-[18px] leading-7 short:leading-6 text-ink-muted">
      {PRE_CONSULT_WELCOME_SUBTITLE}
    </p>
  </div>
);

/**
 * The one thing there is to press, at the bottom of the screen.
 *
 * Its own component, and passed to the shell as a footer rather than written
 * at the end of the card above, because those are two different places on the
 * page: the greeting is centred in the room above, and this is pinned to the
 * floor under it. One block cannot be both.
 *
 * Where it lands is the point. Every step after this one ends in a Continue
 * held at the bottom edge — see `StepActions` — and a welcome that put its own
 * button halfway up would move the target once, on the first press of the
 * form, before settling for the remaining seven screens.
 */
export const PreconsultationWelcomeAction: React.FC<{
  onBegin: () => void;
}> = ({ onBegin }) => (
  // A little air under it on a wide screen, where the design does not stand
  // the action hard against the bottom edge of the window. A phone keeps the
  // column's own padding and nothing more: there the button sits where a thumb
  // already rests.
  <div className="flex justify-start pt-8 sm:justify-center sm:pt-4 sm:pb-16">
    {/* The button is centred by the row around it rather than by `text-center`
        on the block: it is an inline-flex box, and centring text inside it
        would move the label and leave the box where it was.

        The full width of the gutter on a phone, which is what the design draws
        and what a screen ending in a single action wants — a thumb reaching
        the bottom of the glass finds the button wherever it lands. From `sm:`
        up it is the same fixed width as the wizard's Continue, centred in the
        column: a button as wide as a desktop window is not a button, and the
        screen after this one puts its action in exactly that place. */}
    <button
      type="button"
      onClick={onBegin}
      className={
        "inline-flex w-full items-center justify-center gap-3 rounded-[8px] " +
        "bg-[#00BACB] px-7 py-3.5 sm:w-auto sm:min-w-[264px] " +
        "text-[16px] font-medium text-white transition-colors hover:bg-[#00A3B2] " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      }
    >
      {FORM_WELCOME_ACTION}
      <ArrowRight size={18} aria-hidden="true" />
    </button>
  </div>
);

export default PreconsultationWelcome;
